import { useState } from 'react'
import { Box, Button, Divider, Input, type PaletteColor, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { blueGrey, grey } from '@mui/material/colors'
import { toPaletteColor } from '@/common/mui-utils'
import { useDiRef } from '@/common/react/di-hooks'
import { useBehaviorObservable } from '@/common/react/observable-hooks'
import type { CssThemeService } from '@/core/app-shell/CssThemeService'
import { corePropDefs } from '../services/ObjectTypesService'
import {
  PropertyDefinitionChip,
  propertyChipConfig,
} from '../create-type-page/PropertyDefinitionChip'
import { NewPropChipButton } from '../create-type-page/NewPropChipButton'
import {
  isNewLocalProp,
  newLocalPropDef,
  type LocalPropDef,
  type ITypeFormController,
} from './TypeFormController'
import type { PropertyType } from '../gql/PropertyType'

interface TypeFormProps {
  label: string
  initialName: string
  compact?: boolean
  submitLabel: string
  controller: ITypeFormController
}

export function TypeForm({ label, initialName, compact, submitLabel, controller }: TypeFormProps) {
  const cssThemeService = useDiRef<CssThemeService>('CssThemeService')
  const theme = useBehaviorObservable(cssThemeService.theme$)

  const [typeName, setTypeName] = useState(initialName)
  const [customProps, setCustomProps] = useState<LocalPropDef[]>(controller.initialCustomProps)
  const [error, setError] = useState<string | undefined>()

  const customColor = theme.palette.primary
  const builtInColor = toPaletteColor(blueGrey)
  const metaColor = toPaletteColor(grey)

  function onAddProp() {
    setCustomProps((prev) => [...prev, newLocalPropDef('property', 'text')])
  }

  function onDeleteProp(id: number) {
    setCustomProps((prev) => prev.filter((p) => p.id !== id))
  }

  function onPropNameChange(id: number, name: string) {
    setCustomProps((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)))
  }

  function onPropTypeChange(id: number, type: PropertyType) {
    setCustomProps((prev) => prev.map((p) => (p.id === id ? { ...p, type } : p)))
  }

  async function onSubmit() {
    const errorMsg = await controller.onSubmit(typeName, customProps)
    if (errorMsg) setError(errorMsg)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
      >
        <Typography variant="body1">{label}</Typography>
        <CloseIcon
          sx={{
            fontSize: compact ? 28 : 40,
            cursor: 'pointer',
            color: 'error.main',
            '&:hover': { color: 'error.dark', stroke: 'currentColor', strokeWidth: 1 },
          }}
          onClick={() => controller.onClose()}
        />
      </Box>

      <Input
        value={typeName}
        onChange={(e) => setTypeName(e.target.value)}
        disableUnderline
        sx={{
          fontSize: compact ? '2rem' : '3rem',
          borderBottom: '4px solid',
          borderColor: 'primary.main',
          width: 'fit-content',
          mb: 2,
          '& input': {
            width: `${typeName.length + 1}ch`,
            padding: 0,
            lineHeight: 1,
            height: 'auto',
          },
        }}
      />

      <BuiltInSection color={builtInColor} compact={compact} />
      <Divider sx={{ borderColor: builtInColor.light, borderBottomWidth: 2, my: 1 }} />

      <Typography
        variant={compact ? 'body1' : 'h4'}
        sx={{ alignSelf: 'flex-start', mt: compact ? 0.5 : '1em', mb: 1 }}
      >
        Custom properties:
      </Typography>
      <Box className="propChipContainer" sx={{ mb: 2 }}>
        {customProps.map((prop) => (
          <PropertyDefinitionChip
            key={prop.id}
            initialValues={{ name: prop.name, type: prop.type }}
            editable
            typeEditable={isNewLocalProp(prop)}
            compact={compact}
            nameColor={customColor.light}
            typeColor={customColor.main}
            onNameChange={(name) => onPropNameChange(prop.id, name)}
            onTypeChange={(type) => onPropTypeChange(prop.id, type)}
            onCloseClicked={() => onDeleteProp(prop.id)}
          />
        ))}
        <NewPropChipButton onClick={onAddProp} color={customColor.light} compact={compact} />
      </Box>

      <Divider sx={{ borderColor: customColor.light, borderBottomWidth: 2, my: 1 }} />

      <Typography
        variant={compact ? 'body2' : 'body1'}
        sx={{ alignSelf: 'flex-start', mb: 1 }}
      >
        Meta Properties:
      </Typography>
      <Box className="propChipContainer" sx={{ mb: 2 }}>
        {controller.initialMetaProps.map((prop, i) => (
          <PropertyDefinitionChip
            key={i}
            initialValues={{ name: prop.name, type: prop.type }}
            editable={false}
            compact={compact}
            typeColor={metaColor.main}
            nameColor={metaColor.light}
          />
        ))}
      </Box>

      <Divider
        sx={{
          borderColor: error ? 'error.light' : metaColor.light,
          borderBottomWidth: 2,
          my: 1,
        }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 2 }}>
        {error && (
          <Typography
            fontSize={propertyChipConfig.fontSize * 0.8}
            sx={{ color: 'error.light', flex: 1 }}
          >
            {error}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={() => void onSubmit()}
          sx={{
            backgroundColor: 'primary.light',
            fontSize: compact ? 16 : propertyChipConfig.fontSize,
            borderRadius: '8px',
            border: (theme) => `solid 3px ${theme.palette.text.primary}`,
            px: 4,
            py: 1.5,
            width: compact ? 100 : 120,
            height: compact ? 42 : 50,
            '&:hover': { borderColor: 'secondary.main' },
          }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Box>
  )
}

function BuiltInSection({ color, compact }: { color: PaletteColor; compact?: boolean }) {
  return (
    <Box className="propChipContainer">
      <Typography
        variant={compact ? 'body1' : 'h5'}
        gutterBottom
        sx={{ alignSelf: 'flex-start' }}
      >
        Core Properties:
      </Typography>
      {corePropDefs.map((propDef, i) => (
        <PropertyDefinitionChip
          key={i}
          initialValues={propDef}
          editable={false}
          compact={compact}
          nameColor={color.light}
          typeColor={color.dark}
        />
      ))}
    </Box>
  )
}
